import { useState, useEffect } from 'react'
import axios from 'axios'
import InfiniteScroll from 'react-infinite-scroll-component'
import { ADD_GENRES, SET_GENRES } from '@/redux/features/music/searchSlice'
import { useDispatch, useSelector } from 'react-redux'
import TopBar from '@/components/navbar/TopBar'
import BottomBar from '@/components/sidebar/BottomBar'
import { SongCardSkeleton } from '@/components/createSong/CreateSongLoading'
import { useParams } from 'react-router-dom'
import SongCard from '@/components/songCard/LazyLoadSongCard'
import SortFilter from '@/components/sortFilter/SortFilter'

const allSongsSortOptions = [
    {
        value: "latest",
        label: "Most Recent",
    },
    {
        value: "likes",
        label: "Most Liked",
    },
    {
        value: "views",
        label: "Most Played",
    }
]

function Genres() {
    const dispatch = useDispatch()
    const genres = useSelector(state => state.search.genres)
    const [page, setPage] = useState(1)
    const [hasMore, setHasMore] = useState(true)
    const [totalItems, setTotalItems] = useState(0)
    const [genreInfo, setGenreInfo] = useState(null)
    const { genreId } = useParams()
    const { song } = useSelector(state => state.music);
    const [sort, setSort] = useState('latest');

    useEffect(() => {
        fetchGenres()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, genreId, dispatch, sort])

    // Add this new useEffect to reset page number when genreId changes
    useEffect(() => {
        setPage(1)
        setHasMore(true)
    }, [genreId])

    const fetchGenres = async () => {
        try {
            if (page === 1) {
                dispatch(SET_GENRES([]))
            }
            const token = localStorage.getItem('token');
            const response = await axios.get(`/genre?genre=${genreId}&sort=${sort}&page=${page}&limit=20`, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            })
            const { songs, pagination, genre_search } = response.data
            setTotalItems(pagination.total_items)
            setGenreInfo(genre_search)
            if (songs.length === 0 || page >= pagination.total_pages) {
                setHasMore(false)
            }
            dispatch(ADD_GENRES(songs))
        } catch (err) {
            console.error('Error fetching genres:', err)
        }
    }

    const handleSortChange = (sort, page) => {
        setSort(sort);
        setPage(page);
        setHasMore(true);
        dispatch(SET_GENRES([]));
    }

    return (
        <div className={`min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 dark:bg-gradient-to-br dark:from-pink-100 dark:to-pink-300 py-8 pt-[5rem] sm:pt-[6rem] ${song ? "pb-36 md:pb-28" : "pb-24 sm:pb-16"}`}>
            <TopBar heading={genreInfo?.input} />
            <div className="mx-auto px-2 sm:px-4 w-full">
                {/* Header with better integrated sort filter */}
                <div className="mb-4 bg-black/20 dark:bg-white/20 backdrop-blur-sm rounded-xl p-4 sm:py-4 shadow-lg">
                    <div className="flex items-start justify-between mb-2 w-full">
                        <div className="mb-0 min-w-0">
                            <h2 className="w-full truncate text-xl md:text-3xl font-bold font-comfortaa text-white dark:text-black tracking-tight">
                                {genreInfo?.processed_genres?.map(genre => genre.charAt(0).toUpperCase() + genre.slice(1)).join(', ') || 'Genre'}
                                <span className="min-w-0 truncate text-green-400 dark:text-pink-600"> Music</span>
                            </h2>
                            <p className="text-slate-300 dark:text-slate-800 text-xs sm:text-base mt-1">
                                {totalItems.toLocaleString()} tracks available
                            </p>
                        </div>
                        {/* Sort filter aligned right on desktop */}
                        <SortFilter setSort={handleSortChange} sortValue={sort} options={allSongsSortOptions} />
                    </div>
                    {/* Optional divider */}
                    <div className="border-t border-slate-700/50 dark:border-slate-400/30"></div>
                </div>

                {/* Genre Grid */}
                <InfiniteScroll
                    dataLength={genres.length}
                    next={() => setPage(prev => prev + 1)}
                    hasMore={hasMore}
                    loader={<GenreSkeleton />}
                    className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 3xl:grid-cols-6 gap-x-2 xs:gap-x-4 gap-y-4 xs:gap-y-8 sm:px-4 px-2.5"
                >
                    {genres.map((song, i) => (
                        <SongCard song={song} key={i} playingFrom={"genres"} />
                    ))}
                </InfiniteScroll>
            </div>
            <BottomBar />
        </div>
    )
}

const GenreSkeleton = () => {
    return (
        <>
            <SongCardSkeleton />
            <SongCardSkeleton />
            <SongCardSkeleton />
            <SongCardSkeleton />
            <SongCardSkeleton />
            <SongCardSkeleton />
            <SongCardSkeleton />
        </>
    )
}

export default Genres